/**
 * Never a dead end (Matt, 2026-10-17; docs/specs/dead-ends.md, docs/review/dead-ends.md): every reason a check can't run
 * has a remedy, shown on the main result.
 * - `ask`: the missing inputs (the finding's `missing`) are asked for right there, then the check runs again;
 * - `addFiles`: another document is needed (a payslip of another month, the final one): a way back to the upload;
 * - `explain`: the app truly can't check: why (the message) and the concrete next step (`next`, an i18n key);
 * - `info`: nothing is missing and nothing more to check: the result is the answer (e.g. below the pension age);
 * - `notApplicable`: the check doesn't apply to this person or payslip (never shown, src/ui/resultsView.ts).
 * A test (remedies.test.ts) fails when a reason in the code, or a field a check can name as missing, has no remedy.
 */
export type Remedy = { kind: 'ask' } | { kind: 'addFiles' } | { kind: 'explain'; next: NextStep } | { kind: 'info' } | { kind: 'notApplicable' };

/** The concrete next steps of "can't check" (i18n `next.cantCheck.<step>`). */
export const NEXT_STEPS = ['updateApp', 'notSupported', 'checkYourself', 'reserveDuty', 'taxAuthority', 'annualReport', 'yearNotOver', 'yearTooOld', 'viaAuthority', 'depositStatement', 'depositLumpSum'] as const;
export type NextStep = (typeof NEXT_STEPS)[number];

const ask: Remedy = { kind: 'ask' };
const addFiles: Remedy = { kind: 'addFiles' };
const na: Remedy = { kind: 'notApplicable' };
const info: Remedy = { kind: 'info' };
const explain = (next: NextStep): Remedy => ({ kind: 'explain', next });

/** By the reason's own key (after the check's name); a `check.reason` entry wins over it. */
const BY_REASON: Record<string, Remedy> = {
  missingFields: ask,
  estimateMissingFields: ask,
  // The person said the number isn't on their form: a corrected form, or the Tax Authority's calculator.
  notOnForm: explain('taxAuthority'),
  incomeSourcesUnknown: ask,
  // The same reasons when the form's year isn't known (src/form106/signals.ts withNoYear): asked just the same.
  incomeSourcesUnknownNoYear: ask,
  niCertificateNeededNoYear: ask,
  taxStatusesUnknown: ask,
  // Credit points for a degree: the details are asked on the questions step (kind and year; the length of the studies for a later year).
  degreeDetailsMissing: ask,
  dischargeMonthMissing: ask,
  degreeStudyYearsMissing: ask,
  niCertificateNeeded: ask,
  niMonthsMissing: ask,
  regionNeeded: ask,
  noticeDateUnclear: ask,
  estimateOtherEmployer: addFiles,
  finalPayslipNotLoaded: addFiles,
  lastRecuperationNotLoaded: addFiles,
  recuperationMonthsNotLoaded: addFiles,
  quarterNotLoaded: addFiles,
  depositsNotAllLoaded: addFiles,
  needsSeveralPayslips: addFiles,
  // No recuperation line on this payslip (often paid once a year): the payslip with the payment (false-reassurance audit).
  'recuperation.notOnThisPayslip': addFiles,
  noParameters: explain('updateApp'),
  estimateNoParameters: explain('updateApp'),
  paramsOutOfDate: explain('updateApp'),
  statusNotSupported: explain('notSupported'),
  sectorNotSupported: explain('notSupported'),
  recuperationSectorNotSupported: explain('notSupported'),
  sectorDayPay: explain('notSupported'),
  guardingOrder: explain('notSupported'),
  sectorOpenQuestion: explain('notSupported'),
  foreignSectorOpenQuestion: explain('notSupported'),
  caregiverHousingOpen: explain('notSupported'),
  housingCapUnknown: explain('notSupported'),
  uncertainLines: explain('checkYourself'),
  unreadableLines: explain('checkYourself'),
  unitsUnclear: explain('checkYourself'),
  vacationUnitsUnclear: explain('checkYourself'),
  payOutDaysUnclear: explain('checkYourself'),
  wageBaseUnclear: explain('checkYourself'),
  hourlyWageBase: explain('checkYourself'),
  depositsNotRead: explain('checkYourself'),
  globalOnly: explain('checkYourself'),
  notPrinted: explain('checkYourself'),
  // The deposit is never on the payslip: "look at your payslip" can't help (persona audit M2).
  'foreignDeposit.notPrinted': explain('depositStatement'),
  // A printed 0 in a lump-sum sector and month (March to November 2026) proves nothing; the deposit comes later.
  'foreignDeposit.lumpSumLater': explain('depositLumpSum'),
  wageWorkerPayInLieu: explain('checkYourself'),
  partialMonth: explain('checkYourself'),
  reserveDutyMonth: explain('reserveDuty'),
  mostlyReserveDuty: explain('reserveDuty'),
  otherIncomeAnnualReport: explain('annualReport'),
  pensionIncome: explain('taxAuthority'),
  estimateSurtax: explain('taxAuthority'),
  estimateSeparateIncome: explain('taxAuthority'),
  // Foreign workers (Matt, 2026-10-21): not built yet (only in a build with MAGIA_F106_GROUPS=0 since MATT GO 2026-10-26 04:58); keep the Form 106, and the Tax Authority's calculator (a refund can still be due).
  estimateForeignWorker: explain('taxAuthority'),
  creditPointsForeignWorker: explain('taxAuthority'),
  // B2 (behind __F106_GROUPS__, so a build with MAGIA_F106_GROUPS=0 carries none of it; tests and tools read it): the visa is asked
  // right there; no parameters for the year → update the app; a year whose points an unread law may have cancelled → not compared until the law is read.
  ...((__F106_GROUPS__ || !import.meta.env.PROD) ? {
    creditPointsVisaUnknown: ask,
    estimateGroupNoParameters: explain('updateApp'),
    creditPointsGroupYearUnconfirmed: explain('updateApp'),
  } : {}),
  // Olim (behind __OLIM__; none of it in a production build): the month of aliyah is asked on the questions step (missing,
  // or after the form's tax year: a typo to fix there).
  ...((__OLIM__ || !import.meta.env.PROD) ? {
    olimAliyahMonthMissing: ask,
    olimAliyahAfterYear: ask,
  } : {}),
  estimateArrivalYear: explain('taxAuthority'),
  estimateYearNotOver: explain('yearNotOver'),
  // Form 106 year outside the refund window (Matt, 2026-10-23): too old = nothing to do here (no "yet"); a future or unread year is asked again.
  yearTooOld: explain('yearTooOld'),
  yearInFuture: ask,
  yearUnreadable: ask,
  belowMinimumAge: info,
  // No recuperation line here, but another loaded payslip of the employer has the payment: checked on that one.
  'recuperation.paidInAnotherMonth': info,
  // The first 75 days of a job: vacation accrual isn't checked then (docs/rules-spec.md "Vacation"); it is from then on.
  'vacation.firstDays': info,
  sameAsMinimumWage: info,
  depositInstead: info,
  paidAtEndOfEmployment: info,
  noPayThisMonth: na,
  notLeft: na,
  shownOnLastPayslip: na,
  shownOnLatestMonth: na,
  notForeignWorker: na,
  notPensioner: na,
  notCleaningWorker: na,
  notDismissed: na,
  noOvertime: na,
  notChecked: na,
};

/** The remedy for a can't-check result's message key (`check.reason`), or undefined when it has none (a test fails). */
export function remedyFor(messageKey: string): Remedy | undefined {
  const reason = messageKey.slice(messageKey.indexOf('.') + 1);
  return BY_REASON[messageKey] ?? BY_REASON[reason];
}

/**
 * How a missing field or answer is asked for (src/ui/missingInfo.ts): typed values (`money`, `count`, `month`, `date`,
 * `year`, `months12`, `leaveBalance`: previous balance, used, balance), an existing question (`answer`), the National Insurance certificate entry (`niCertificate`), or
 * not typable in a box (lists of payslip lines): then the finding says to check that item on the payslip yourself.
 */
export type FieldInput = 'money' | 'count' | 'month' | 'date' | 'year' | 'months12' | 'leaveBalance' | 'answer' | 'niCertificate' | 'notTypable';
export const MISSING_INPUTS: Record<string, FieldInput> = {
  // Payslip values
  baseSalary: 'money', grossPay: 'money', hourlyRate: 'money', pensionEmployee: 'money', pensionEmployer: 'money',
  severanceEmployer: 'money', foreignDeposit: 'money', healthTax: 'money', incomeTax: 'money', nationalInsurance: 'money', netPay: 'money', printedMinimumWage: 'notTypable',
  hoursPaid: 'count', daysWorked: 'count', recuperationDays: 'count', payMonth: 'month', startDate: 'date',
  paymentLines: 'notTypable', deductionLines: 'notTypable', overtimeLines: 'notTypable',
  // A leave balance as payslips print it: the previous balance, the days used, the balance now (the month's accrual follows).
  vacationBalance: 'leaveBalance', sickBalance: 'leaveBalance',
  // Form 106 values
  taxYear: 'year', monthsWorked: 'months12', months: 'months12', salary: 'money', taxWithheld: 'money', creditPoints: 'count',
  // Answers (their question, right there)
  jobPercent: 'answer', payBasis: 'answer', sector: 'answer', hadPensionAtHire: 'answer', workerType: 'answer', caregiverEmployer: 'answer',
  housingRegion: 'answer', contractorSector: 'answer', leavingReason: 'answer', noticeDate: 'answer', birthDate: 'answer', adjustedMinimumPercent: 'answer',
  incomeSources: 'answer', taxStatuses: 'answer', niCertificates: 'niCertificate',
  // Credit points for a degree: its kind, the year the studies ended and their length, under "degree" on the questions step.
  degreeKind: 'answer', degreeYear: 'answer', degreeStudyYears: 'answer', dischargeMonth: 'answer',
  // B2 (behind __F106_GROUPS__; none of it in a build with MAGIA_F106_GROUPS=0): a foreign worker's visa in the tax year.
  ...((__F106_GROUPS__ || !import.meta.env.PROD) ? { visaYears: 'answer' as const } : {}),
  // Olim (behind __OLIM__): the month of aliyah, under "new immigrant" on the questions step.
  ...((__OLIM__ || !import.meta.env.PROD) ? { aliyahMonth: 'answer' as const } : {}),
};

/**
 * "Not sure" must lead somewhere (dead ends part 8; docs/review/questions.md): every answer a check can ask for has a
 * hint under it (i18n `missing.notSure.<key>`): where to find the answer, and what happens until then (only that check
 * is skipped, or the stated safer assumption). A test fails when an answer in MISSING_INPUTS has none.
 */
export const NOT_SURE_HINTS: readonly string[] = Object.entries(MISSING_INPUTS).filter(([, v]) => v === 'answer' || v === 'niCertificate').map(([k]) => k);

/**
 * Generic "couldn't read" wording (tools/deadEnds.ts unreadTexts; Matt, 2026-10-18) and how each text reaches the
 * person, so none is ever shown bare:
 * - `fields`: shown next to the values it's about, each named, with an input for each (the fill-in box, the scan's
 *   review fields, a file row's period box, a "Check your answers" row with Change);
 * - `document`: about a whole document not read: shown with the way on right there (read the scan, type it in);
 * - `answer`: an answer option or a hint for a question, not a report that something wasn't read;
 * - `nextStep`: nothing can be typed (the translation's list of lines): says what to do instead;
 * - `about`: describes the app (the privacy page), not anything on the screen.
 * A browser check (tests/ui/bareUnread.ts) fails when such text is on screen without an input or button beside it.
 */
export type UnreadRemedy = 'fields' | 'document' | 'answer' | 'nextStep' | 'about';
const NOT_SURE_KEYS = ['birthDate', 'payBasis', 'sector', 'jobPercent', 'hadPensionAtHire', 'workerType', 'caregiverEmployer', 'contractorSector', 'housingRegion', 'leavingReason', 'noticeDate', 'adjustedMinimumPercent', 'incomeSources', 'taxStatuses', 'degreeKind', 'degreeYear', 'degreeStudyYears', 'dischargeMonth'];
export const UNREAD_TEXTS: Record<string, UnreadRemedy> = {
  // The Form 106 other-income stop: "not sure" is one of the answers it names, and it says what to do (an annual tax return).
  'flow.early.incomeSource.otherIncome': 'answer',
  // The sex question's help: says what "Prefer not to say" costs (the question itself is the answer to give).
  'f106.q.sex.help': 'answer',
  // The degree questions' help: says what leaving the answer empty does.
  'f106.q.degreeYear.help': 'answer', 'f106.q.degreeStudyYears.help': 'answer', 'f106.q.dischargeMonth.help': 'answer', 'f106.q.serviceLong.help': 'answer',
  // The single-parent and disabled-child follow-ups' help: says what leaving the answer empty does.
  'f106.q.singleParentKind.help': 'answer', 'f106.q.disabledChildren.help': 'answer',
  // B2 (on unless MAGIA_F106_GROUPS=0): the visa question's "I'm not sure" option (its hint missing.notSure.visaYears says what happens).
  ...((__F106_GROUPS__ || !import.meta.env.PROD) ? { 'f106.visaYear.notSure': 'answer' as const, 'f106.monthsInIsrael.notSure': 'answer' as const, 'flow.early.visaYear.notSure': 'answer' as const } : {}),
  // Olim (preview only): the aliyah question's help and its "not sure" hint say what leaving it empty does.
  ...((__OLIM__ || !import.meta.env.PROD) ? { 'f106.q.olimAliyah.help': 'answer' as const, 'missing.notSure.aliyahMonth': 'answer' as const } : {}),
  'field.pensionEmployee': 'fields', 'field.pensionEmployer': 'fields', 'field.severanceEmployer': 'fields',
  'upload.monthNotFound': 'fields', 'upload.period.monthHelp': 'fields', 'upload.taxYearNotFound': 'fields', 'upload.period.yearHelp': 'fields',
  'results.payslipHeadingNotFound': 'fields', 'step.payslipCompleteness.noPayMonth': 'fields',
  // Unreadable payment lines: the finding sends the person to the payslip itself.
  'payslipCompleteness.unreadableLines': 'document', 'step.payslipCompleteness.unreadableLines': 'document',
  'sick.unreadableLines': 'document', 'vacation.unreadableLines': 'document', 'overtime.unreadableLines': 'document', 'recuperation.unreadableLines': 'document',
  'leavingPay.unreadableLines': 'document', 'notice.unreadableLines': 'document', 'minimumWage.unreadableLines': 'document',
  'next.cannot_check': 'fields', 'ocr.blurred': 'fields', 'ocr.lowResolution': 'fields', 'ocr.gate.missingNamed': 'fields', 'ocr.gate.minimumWage': 'fields',
  'ocr.minimumWageHeld': 'fields', 'ocr.noCrop': 'fields', 'ocr.needed.help': 'fields', 'severance.wageBaseUnclear': 'fields', 'severance.depositsNotRead': 'fields',
  'missing.heading': 'fields', 'missing.pleaseCheck': 'fields', 'cya.notRead': 'fields', 'cya.readUnsure': 'fields', 'cya.summary.notRead': 'fields',
  'f106.headingNoYear': 'fields', 'cya.scanNote': 'fields', 'cya.scanNotePdf': 'fields', 'f106.scan.scannedPdf': 'fields', 'f106.scan.photo': 'fields', 'minimumWageData.missingFields': 'fields', 'form106.missingFields': 'fields',
  'form106.estimateMissingFields': 'fields', 'form106.yearUnreadable': 'fields', 'f106.reason.yearUnreadable': 'fields',
  // The months picker (src/ui/monthsPicker.ts): each beside the 12 month boxes.
  'f106.monthsPicker.instructionUnsure': 'fields', 'f106.monthsPicker.unsure': 'fields', 'f106.monthsPicker.noRow': 'fields',
  'upload.status.scanToRead': 'document', 'upload.status.scanFailed': 'document', 'ocr.failed.nothingRead': 'document', 'ocr.failed.notForm106': 'document',
  'unknownFormat.text.form106': 'document', 'upload.status.notRead': 'document', 'upload.cannotRead': 'document', 'upload.cannotRead.many': 'document',
  'upload.status.notRecognised': 'document', 'upload.reason.unknownLayout.readGate': 'document', 'upload.status.payslipsNotRead': 'document', 'upload.status.pagesNoText': 'document', 'leave.what.files': 'document',
  'questions.dontKnow': 'answer', 'diag.notSure': 'answer', 'questions.workWeekDays.help': 'answer',
  'f106.source.rentOther': 'answer', 'f106.source.otherIncome': 'answer', ...Object.fromEntries(NOT_SURE_KEYS.map((k) => [`missing.notSure.${k}`, 'answer' as const])),
  'tv.onlyRead': 'nextStep', 'tv.onlyRead106': 'nextStep', 'results.betaVendor': 'nextStep', 'upload.status.doubtfulToRead': 'document',
  'page.privacy.reportFiles': 'about', 'form106.creditPointsAboveBasic': 'about',
};

/**
 * A file read from a known layout but turned away by the read gate (Matt, 2026-10-21; src/upload/readGate.ts,
 * src/form106/readGate.ts): both main figures not read, most figures not read, or figures that don't fit together. Its row
 * and the upload step say the file isn't recognised (upload.reason.unknownLayout, .form106), and each reason's ways on
 * are shown right there: `sendLayout` the unknown-format offer (its anonymous layout in the diagnostic report),
 * `typeIn` the numbers typed in instead (the Form 106 form, or the payslip form), `otherFile` choosing another file.
 * A test (remedies.test.ts) fails when a reason the gate can give has no way on, or lacks the typing-in one.
 */
export type FileWayOn = 'sendLayout' | 'typeIn' | 'otherFile';
export const READ_GATE_REMEDIES: Record<'mainFigures' | 'mostUnread' | 'inconsistent', readonly FileWayOn[]> = {
  mainFigures: ['sendLayout', 'typeIn', 'otherFile'],
  mostUnread: ['sendLayout', 'typeIn', 'otherFile'],
  inconsistent: ['sendLayout', 'typeIn', 'otherFile'],
};

/**
 * Upload-step states where Continue waits for the person (Matt, 2026-10-23 19:29, item E), each with its ways on, right
 * there on the step (never a dead end):
 * - `duplicateDocument`: the same payslip or Form 106 read from two files (a PDF and a photo of it;
 *   src/upload/duplicates.ts): `chooseHere` the question "Which one should we use?" with each file as an option (its
 *   missing-input line names it when Continue is pressed), or `removeFile` a file's own ✕ on its row.
 * Its texts (flowD messages) are listed so a test fails if one is gone in English or Hebrew.
 */
export type UploadWayOn = 'chooseHere' | 'removeFile';
export const UPLOAD_WAITS: Record<'duplicateDocument', { wayOn: readonly UploadWayOn[]; texts: readonly string[] }> = {
  duplicateDocument: { wayOn: ['chooseHere', 'removeFile'], texts: ['upload.dup.payslip', 'upload.dup.form106', 'upload.dup.help', 'upload.dup.needed.payslip', 'upload.dup.needed.form106', 'upload.dup.neededMessage'] },
};
